/**
 * Hand-rolled chart → PNG export (no extra dependencies).
 *
 * Charts render as live SVG whose paint comes from CSS classes, so a naive
 * `XMLSerializer` round-trip loses every color and font. The clone therefore
 * carries the *computed* value of a small whitelist of presentation properties
 * inlined as attributes before serialization — after that the SVG is a
 * self-contained document any `<img>` can rasterize.
 *
 * The canvas is drawn at 2x for retina-crisp screenshots and gets a header band
 * above the plot carrying the card's title + timeframe caption, so a pasted PNG
 * still says what it is and over what window.
 *
 * The on-screen legend is HTML chips *outside* the SVG, so it can never come
 * along in the serialization — the exported lines would be unlabeled. Callers
 * therefore hand over their visible series and a key is drawn onto the canvas
 * below the plot, wrapping to as many rows as it needs.
 */

/** Presentation properties that must survive the CSS → attribute flattening. */
const INLINE_PROPS = [
  "fill",
  "fill-opacity",
  "stroke",
  "stroke-width",
  "stroke-opacity",
  "stroke-dasharray",
  "stroke-linecap",
  "stroke-linejoin",
  "opacity",
  "font-family",
  "font-size",
  "font-weight",
  "font-variant-numeric",
  "letter-spacing",
  "text-anchor",
  "dominant-baseline",
] as const;

const FONT_STACK = '"Plus Jakarta Sans", system-ui, -apple-system, "Segoe UI", sans-serif';

/* ----------------------------- key (legend) geometry ----------------------- */

/** Matches the title band's stack; `tabular-nums` has no canvas equivalent. */
const KEY_FONT = `400 12px ${FONT_STACK}`;
const KEY_INK = "#64748b"; // slate-500 — muted ink
const SWATCH_WIDTH = 14;
const SWATCH_TEXT_GAP = 6;
const ENTRY_GAP = 24;
const ROW_GAP = 8;
const ROW_HEIGHT = 14;
/** Breathing room above the first key row and below the last. */
const KEY_PAD = 16;

/** One series in the exported key. */
export interface ChartLegendEntry {
  label: string;
  color: string;
  /** Dashed swatch — mirrors a dashed reference line (e.g. the field median). */
  dashed?: boolean;
  /** Latest value, already formatted with any suffix (e.g. `"4.2%"`). */
  value?: string;
}

interface PlacedEntry extends ChartLegendEntry {
  text: string;
  x: number;
  row: number;
}

/**
 * Flows key entries left-to-right, wrapping to a new row once one would run past
 * `maxWidth`. Needs a `ctx` whose font is already `KEY_FONT` to measure with.
 */
function layoutKey(
  ctx: CanvasRenderingContext2D,
  entries: ChartLegendEntry[],
  maxWidth: number,
): { placed: PlacedEntry[]; rows: number } {
  const placed: PlacedEntry[] = [];
  let row = 0;
  let x = 0;
  for (const entry of entries) {
    const text = entry.value ? `${entry.label} — ${entry.value}` : entry.label;
    const entryWidth = SWATCH_WIDTH + SWATCH_TEXT_GAP + ctx.measureText(text).width;
    if (x > 0 && x + entryWidth > maxWidth) {
      row += 1;
      x = 0;
    }
    placed.push({ ...entry, text, x, row });
    x += entryWidth + ENTRY_GAP;
  }
  return { placed, rows: placed.length > 0 ? row + 1 : 0 };
}

/** Total vertical space a key of `rows` rows occupies, padding included. */
function keyBlockHeight(rows: number): number {
  return rows === 0 ? 0 : KEY_PAD * 2 + rows * ROW_HEIGHT + (rows - 1) * ROW_GAP;
}

/** Paints the laid-out key with `top` as the block's outer top edge. */
function drawKey(
  ctx: CanvasRenderingContext2D,
  placed: PlacedEntry[],
  left: number,
  top: number,
): void {
  ctx.font = KEY_FONT;
  ctx.textBaseline = "middle";
  ctx.lineWidth = 2;
  ctx.lineCap = "butt";
  for (const entry of placed) {
    const midY = top + KEY_PAD + entry.row * (ROW_HEIGHT + ROW_GAP) + ROW_HEIGHT / 2;
    ctx.beginPath();
    ctx.setLineDash(entry.dashed ? [4, 3] : []);
    ctx.strokeStyle = entry.color;
    ctx.moveTo(left + entry.x, midY);
    ctx.lineTo(left + entry.x + SWATCH_WIDTH, midY);
    ctx.stroke();
    ctx.fillStyle = KEY_INK;
    ctx.fillText(entry.text, left + entry.x + SWATCH_WIDTH + SWATCH_TEXT_GAP, midY);
  }
  ctx.setLineDash([]);
}

/** Copies computed presentation styles from every source node onto its clone. */
function inlineComputedStyles(source: Element, clone: Element): void {
  const computed = window.getComputedStyle(source);
  for (const prop of INLINE_PROPS) {
    const value = computed.getPropertyValue(prop);
    if (value && value !== "none" && value !== "normal") clone.setAttribute(prop, value);
    // `fill: none` is meaningful on a line path — keep it, drop the rest.
    else if (value === "none" && (prop === "fill" || prop === "stroke")) clone.setAttribute(prop, "none");
  }
  const sourceKids = source.children;
  const cloneKids = clone.children;
  for (let i = 0; i < sourceKids.length && i < cloneKids.length; i++) {
    inlineComputedStyles(sourceKids[i]!, cloneKids[i]!);
  }
}

/** `"Legal services"`, `"sentiment"` → `"legal-services-sentiment-2026-07-29.png"`. */
export function chartFileName(topic: string, chart: string, now: Date = new Date()): string {
  const slug = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "chart";
  const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return `${slug(topic)}-${slug(chart)}-${date}.png`;
}

interface ExportOptions {
  /** Headline drawn above the plot (the card's title). */
  title: string;
  /** Second line under the title (timeframe / lens caption). */
  caption?: string;
  /** Download filename — see `chartFileName`. */
  fileName: string;
  /**
   * Key drawn under the plot — the VISIBLE series only, in their on-screen
   * order. Pass the lone series for single-series charts too: the file stands
   * alone, so its line still needs a name.
   */
  legend?: ChartLegendEntry[];
  /** Device-pixel multiplier. 2 = retina-crisp. */
  scale?: number;
  /** Canvas background; charts render on white cards. */
  background?: string;
}

/**
 * Rasterizes one rendered chart SVG to a PNG and triggers a download.
 *
 * @param svg Live SVG element as rendered in the page.
 * @param options Title/caption/filename and rasterization settings.
 * @return Resolves once the download has been triggered.
 */
export async function exportChartPng(svg: SVGSVGElement, options: ExportOptions): Promise<void> {
  const { title, caption, fileName, legend = [], scale = 2, background = "#ffffff" } = options;

  const rect = svg.getBoundingClientRect();
  const width = Math.max(1, Math.round(rect.width || svg.clientWidth || 800));
  const height = Math.max(1, Math.round(rect.height || svg.clientHeight || 300));

  const clone = svg.cloneNode(true) as SVGSVGElement;
  inlineComputedStyles(svg, clone);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));
  if (!clone.getAttribute("viewBox")) clone.setAttribute("viewBox", `0 0 ${width} ${height}`);
  // Class-based styling is gone once inlined; strip it so nothing dangles.
  clone.querySelectorAll("[class]").forEach((el) => el.removeAttribute("class"));

  const markup = new XMLSerializer().serializeToString(clone);
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;

  const image = new Image();
  image.decoding = "sync";
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("Could not rasterize the chart SVG."));
    image.src = url;
  });

  const padX = 20;
  const headerHeight = caption ? 60 : 42;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is unavailable in this browser.");

  // Measure first: how many rows the key wraps to decides the canvas height.
  // Sizing the canvas resets every ctx setting, so this happens before that.
  ctx.font = KEY_FONT;
  const { placed, rows: keyRows } = layoutKey(ctx, legend, Math.max(1, width - padX * 2));
  // The 12px keeps its old job as the bottom pad when there is no key at all.
  const footerHeight = keyRows > 0 ? keyBlockHeight(keyRows) : 12;
  const totalHeight = height + headerHeight + footerHeight;

  canvas.width = width * scale;
  canvas.height = totalHeight * scale;
  ctx.scale(scale, scale);
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, totalHeight);

  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#1a1640"; // brand-950 — primary ink
  ctx.font = `600 15px ${FONT_STACK}`;
  ctx.fillText(title, padX, 26);
  if (caption) {
    ctx.fillStyle = "#64748b"; // slate-500 — secondary ink
    ctx.font = `400 12px ${FONT_STACK}`;
    ctx.fillText(caption, padX, 46);
  }

  ctx.drawImage(image, 0, headerHeight, width, height);

  if (keyRows > 0) drawKey(ctx, placed, padX, headerHeight + height);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("Could not encode the chart PNG.");

  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke on the next tick — Safari cancels the download if the URL dies first.
  setTimeout(() => URL.revokeObjectURL(href), 4000);
}
